import { Stack, Typography } from "@mui/material";
import { BuyerJourneyOption } from "../../types/enums";
import { ReuseTooltip } from "./ReuseTooltip";
import { ArrowIcon } from "../images/ArrowIcon";
import { formatDateRange } from "../../utils/formatDateRange";

const mapper: Record<
  BuyerJourneyOption,
  { color: string; backgroundColor: string; label: BuyerJourneyOption }
> = {
  [BuyerJourneyOption.target]: {
    color: "#2194D2",
    backgroundColor: "#D3EAF6",
    label: BuyerJourneyOption.target,
  },
  [BuyerJourneyOption.awareness]: {
    color: "#1A76A8",
    backgroundColor: "#D3EAF6",
    label: BuyerJourneyOption.awareness,
  },
  [BuyerJourneyOption.consideration]: {
    color: "#14597E",
    backgroundColor: "#BCDFF2",
    label: BuyerJourneyOption.consideration,
  },
  [BuyerJourneyOption.decision]: {
    color: "#14597E",
    backgroundColor: "#A6D4ED",
    label: BuyerJourneyOption.decision,
  },
  [BuyerJourneyOption.purchase]: {
    color: "#CB6D2B",
    backgroundColor: "#FFCFAF",
    label: BuyerJourneyOption.purchase,
  },
};

const TooltipBlock = ({
  prevType,
  currentType,
  last_updated,
}: {
  prevType?: BuyerJourneyOption;
  currentType: BuyerJourneyOption;
  last_updated: string;
}) => {
  const prev = prevType && mapper[prevType];
  const current =
    currentType !== BuyerJourneyOption.target && mapper[currentType];

  return (
    <Stack gap={"8px"} padding={"4px 2px"}>
      {(prev || current) && (
        <Stack flexDirection={"row"} gap={"4px"}>
          {prev && (
            <>
              <Stack
                sx={{
                  borderRadius: "8px",
                  backgroundColor: prev.backgroundColor,
                  padding: "4px 6px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    lineHeight: "12px",
                    color: prev.color,
                    fontWeight: 500,
                    textDecoration: "line-through",
                  }}
                >
                  {prev.label}
                </Typography>
              </Stack>
              <ArrowIcon rotate={"45"} color={"white"} />
            </>
          )}

          {current && (
            <Stack
              sx={{
                borderRadius: "8px",
                backgroundColor: current.backgroundColor,
                padding: "4px 6px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "12px",
                  lineHeight: "12px",
                  color: current.color,
                  fontWeight: 500,
                }}
              >
                {current.label}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      {last_updated && (
        <Typography
          sx={{
            fontSize: "12px",
            lineHeight: "12px",
            color: "#FFFFFF",
            fontWeight: 500,
          }}
        >
          Last update: {formatDateRange(last_updated, true)}
        </Typography>
      )}
    </Stack>
  );
};

const Indicator = ({ type }: { type: BuyerJourneyOption }) => {
  const { color, backgroundColor, label } = mapper[type];

  return (
    <Stack
      sx={{
        borderRadius: "8px",
        backgroundColor,
        padding: "4px 8px",
      }}
    >
      <Typography
        sx={{
          fontSize: "14px",
          lineHeight: "20px",
          color,
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
};

export const BuyerJourney = ({
  data,
}: {
  data?: {
    prev_type?: BuyerJourneyOption;
    current_type: BuyerJourneyOption;
    last_updated: string;
  };
}) => {
  if (!data)
    return (
      <Stack
        sx={{
          alignItems: "flex-start",
        }}
      >
        <ReuseTooltip
          text={
            "Buyer's Journey stage is unavailable for manually uploaded accounts."
          }
        >
          --
        </ReuseTooltip>
      </Stack>
    );

  const { prev_type, current_type, last_updated } = data;

  return (
    <Stack
      sx={{
        alignItems: "flex-start",
      }}
    >
      <ReuseTooltip
        text={
          <TooltipBlock
            last_updated={last_updated}
            currentType={current_type}
            prevType={prev_type}
          />
        }
      >
        <Indicator type={current_type} />
      </ReuseTooltip>
    </Stack>
  );
};
